// src/firebase/config.ts
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';

// Environment detection
const getEnvironment = () => {
  if (typeof window !== 'undefined' && (window as any).FIREBASE_CONFIG) {
    return 'runtime'; // Server-injected config (Heroku)
  }
  
  const nodeEnv = process.env.REACT_APP_NODE_ENV || process.env.NODE_ENV;
  return nodeEnv === 'production' ? 'production' : 'staging';
};

// Runtime configuration - works for local, staging, and production
const getFirebaseConfig = () => {
  const environment = getEnvironment();
  
  // Try to get config from window object (set by server) first - for Heroku deployments
  if (environment === 'runtime' && typeof window !== 'undefined' && (window as any).FIREBASE_CONFIG) {
    console.log('Using server-injected Firebase config (Heroku)');
    return (window as any).FIREBASE_CONFIG;
  }

  // For local development, use environment-specific variables
  console.log(`Using build-time Firebase environment variables for: ${environment}`);
  
  if (environment === 'production') {
    return {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY_PRODUCTION,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN_PRODUCTION,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID_PRODUCTION,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET_PRODUCTION,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID_PRODUCTION,
      appId: process.env.REACT_APP_FIREBASE_APP_ID_PRODUCTION,
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID_PRODUCTION,
    };
  } else {
    // Default to staging (for local development and staging)
    return {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY_STAGING,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN_STAGING,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID_STAGING,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET_STAGING,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID_STAGING,
      appId: process.env.REACT_APP_FIREBASE_APP_ID_STAGING,
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID_STAGING,
    };
  }
};

const firebaseConfig = getFirebaseConfig();

// Validate configuration with better error messages
if (!firebaseConfig.apiKey) {
  console.error('Firebase API Key is missing');
  console.error('Environment detection:', getEnvironment());
  console.error('Available environment variables:', {
    'STAGING API Key': !!process.env.REACT_APP_FIREBASE_API_KEY_STAGING,
    'PRODUCTION API Key': !!process.env.REACT_APP_FIREBASE_API_KEY_PRODUCTION,
    'NODE_ENV': process.env.NODE_ENV,
    'REACT_APP_NODE_ENV': process.env.REACT_APP_NODE_ENV,
  });
  throw new Error('Firebase API Key is missing. Please check your environment variables.');
}

if (!firebaseConfig.authDomain) {
  console.error('Firebase Auth Domain is missing');
  throw new Error('Firebase Auth Domain is missing. Please check your environment variables.');
}

console.log('Firebase config loaded successfully:', {
  environment: getEnvironment(),
  apiKey: firebaseConfig.apiKey ? '? Present' : '? Missing',
  authDomain: firebaseConfig.authDomain ? '? Present' : '? Missing',
  projectId: firebaseConfig.projectId ? '? Present' : '? Missing',
});

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication and get a reference to the service
export const auth = getAuth(app);

export default app;